import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
  Avatar,
  CircularProgress,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useSnackbar } from "../SnackBar";
import CustomTable from "../CustomTable";
import CustomAccordion from "../CustomAccordion";
import SkeletonTable from "../Skeletons/SkeletonTable";
import SkeletonAccordion from "../Skeletons/SkeletonAccordion";
import CustomTooltip from "../CustomTooltip";
import { useTranslation } from "react-i18next";
import CustomMenu from "../CustomMenu";
import { removeButtonStyle, acceptButtonStyle, styles } from "./styles";
import endpoints from "../../Helpers/Endpoints";
import { apiPostRequest } from "../../Helpers/Requests";

const MyMembersCard = ({
  members,
  openModal,
  activeTab,
  sortOrder,
  toggleSortOrder,
  isLoading,
  containerRef,
  setMembers,
  isModerator,
}) => {
  const isMobile = useMediaQuery("(max-width:767px)");
  const showSnackbar = useSnackbar();
  const [expandedId, setExpandedId] = useState(null);
  const { t } = useTranslation("my-members-card");

  const {
    contactIconsContainer,
    tooltipStyles,
    actionButtonsContainer,
    pendingActionsContainer,
    moderatorBadgeOverlay,
    nameColumnWrapper,
    nameTextWrapper,
    nameTypography,
    mobileSummaryName,
    sortHeader,
    mobileAccordionContainer,
  } = styles;
  const { promoteMemberToModerator, demoteModerator } = endpoints;
  const [loading, setLoading] = useState(false);

  const permoteAction = async (isModerator, memberId) => {
    try {
      setLoading(true);

      const actionApi = isModerator
        ? demoteModerator
        : promoteMemberToModerator;
      const response = await apiPostRequest(actionApi(memberId));
      if (response.data.success) {
        showSnackbar(isModerator ? t("demoted") : t("promoted"), "success");
        if (setMembers) {
          setMembers((prevMembers) => {
            return prevMembers.map((member) =>
              member.id === memberId
                ? { ...member, is_moderator: !isModerator }
                : member,
            );
          });
        }
      } else {
        showSnackbar(response.data.error || "Action failed", "error");
      }
    } catch (error) {
      showSnackbar(error.response.data.error, "error");
    } finally {
      setLoading(false);
    }
  };
  const formatDate = (dateStr) => (dateStr ? dateStr.replace(/\//g, ".") : "-");

  const getContactIcons = (member) => {
    const { email, phonePrefix, phoneNumber } = member;
    const phone = [phonePrefix, phoneNumber].filter(Boolean).join("");
    return (
      <Box sx={contactIconsContainer}>
        <CustomTooltip
          title={t("tootip_email")}
          tooltipSxStyles={tooltipStyles}
        >
          <IconButton
            onClick={() => {
              navigator.clipboard.writeText(email);
              showSnackbar(t("member_copy_email"), "success");
            }}
          >
            <img src="/icons/mail-forward-black.svg" alt="mail" />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip
          title={t("tootip_phone")}
          tooltipSxStyles={tooltipStyles}
        >
          <IconButton
            onClick={() => {
              navigator.clipboard.writeText(phone);
              showSnackbar(t("member_copy_phone"), "success");
            }}
          >
            <img src="/icons/phone-outgoing-black.svg" alt="phone" />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip title={t("whatsapp")} tooltipSxStyles={tooltipStyles}>
          <IconButton
            onClick={(e) => {
              e.preventDefault();
              window.open(
                `https://api.whatsapp.com/send/?phone=${phone}&text=hello`,
                "_blank",
                "noopener,noreferrer",
              );
            }}
          >
            <img src="/icons/whatsapp-icon-black.svg" alt="whatsapp" />
          </IconButton>
        </CustomTooltip>
      </Box>
    );
  };

  const getActionButtons = (member) =>
    activeTab === 1 ? (
      member.is_community_manager ? null : (
        <Box sx={actionButtonsContainer}>
          <Button
            sx={removeButtonStyle}
            onClick={() => openModal(member, "remove")}
          >
            {t("remove")}
          </Button>

          {!isModerator ? (
            isMobile ? (
              <Button
                sx={member.is_moderator ? removeButtonStyle : acceptButtonStyle}
                onClick={() => permoteAction(member.is_moderator, member.id)}
                disabled={loading}
              >
                {member.is_moderator
                  ? t("demote_moderator")
                  : t("promote_to_moderator")}
                {loading && (
                  <CircularProgress
                    sx={{
                      color: "#248B70",
                      ml: 1,
                    }}
                    size={16}
                  />
                )}
              </Button>
            ) : (
              <CustomMenu
                menuBtnIconPath="/menu_icon.svg"
                menuItems={[
                  member.is_moderator
                    ? {
                        keepOpenOnLoading: true,
                        loading,
                        text: t("demote_moderator"),
                        onClick: () =>
                          permoteAction(member.is_moderator, member.id),
                      }
                    : {
                        keepOpenOnLoading: true,
                        loading,
                        text: t("promote_to_moderator"),
                        onClick: () =>
                          permoteAction(member.is_moderator, member.id),
                      },
                ]}
              />
            )
          ) : null}
        </Box>
      )
    ) : (
      <Box sx={pendingActionsContainer}>
        <Button
          sx={acceptButtonStyle}
          onClick={() => openModal(member, "accept")}
        >
          {t("accept")}
        </Button>
        <Button
          sx={removeButtonStyle}
          onClick={() => openModal(member, "reject")}
        >
          {t("ignore")}
        </Button>
      </Box>
    );

  const getMemberImage = (member) => (
    <Box position="relative">
      <Avatar
        alt={member.full_name}
        src={member.image_url}
        sx={{ width: 32, height: 32 }}
      />
      {activeTab === 1 && member.is_moderator ? (
        <Box sx={moderatorBadgeOverlay}>
          <img
            src="/icons/tie-icon.svg"
            alt="verified"
            width="100%"
            height="100%"
          />
        </Box>
      ) : null}
    </Box>
  );

  const columns = [
    {
      id: "name",
      label: t("table_columns.name"),
      render: (member) => (
        <Box sx={nameColumnWrapper}>
          {getMemberImage(member)}
          <Box sx={nameTextWrapper}>
            <CustomTooltip
              title={member.full_name}
              tooltipSxStyles={tooltipStyles}
            >
              <Typography sx={nameTypography}>{member.full_name}</Typography>
            </CustomTooltip>
          </Box>
        </Box>
      ),
      headerStyle: { width: "310px" },
      cellStyle: { width: "310px" },
    },
    {
      id: "location",
      label: t("table_columns.location"),
      render: (member) => (
        <CustomTooltip
          title={member.residential_address}
          tooltipSxStyles={tooltipStyles}
        >
          {member.residential_address.length > 25
            ? member.residential_address.slice(0, 25) + "..."
            : member.residential_address}
        </CustomTooltip>
      ),
    },
    {
      id: "date",
      label:
        activeTab === 1
          ? t("table_columns.joined_on")
          : t("table_columns.request_date"),
      render: (member) => formatDate(member.join_date),
      headerRender: () => (
        <Box onClick={toggleSortOrder} sx={sortHeader}>
          {activeTab === 1
            ? t("table_columns.joined_on")
            : t("table_columns.request_date")}
          {sortOrder === "asc" ? (
            <KeyboardArrowDownIcon />
          ) : (
            <KeyboardArrowUpIcon />
          )}
        </Box>
      ),
      headerStyle: { cursor: "pointer", userSelect: "none" },
    },
    {
      id: "contact",
      label: t("table_columns.contact"),
      render: (member) => getContactIcons(member),
    },
    ...(activeTab === 1
      ? [
          {
            id: "activity",
            label: t("table_columns.last_activity"),
            render: (member) => formatDate(member.last_login),
          },
        ]
      : []),
    {
      id: "actions",
      label: t("table_columns.actions"),
      render: (member) => getActionButtons(member),
    },
  ];

  const mobileDetails = (member) => [
    { label: t("table_columns.location"), value: member.residential_address },
    {
      label:
        activeTab === 1
          ? t("table_columns.joined_on")
          : t("table_columns.request_date"),
      value: formatDate(member.join_date),
    },
    { label: t("table_columns.contact"), value: getContactIcons(member) },
    ...(activeTab === 1
      ? [
          {
            label: t("table_columns.last_activity"),
            value: formatDate(member.last_login),
          },
        ]
      : []),
  ];

  const handleChange = (memberId) => (event, isExpanded) => {
    setExpandedId(isExpanded ? memberId : null);
  };

  return (
    <>
      {isMobile ? (
        isLoading ? (
          <SkeletonAccordion />
        ) : (
          <Box
            ref={containerRef}
            className="custom-scrollbar"
            sx={mobileAccordionContainer}
          >
            {members.map((member) => (
              <CustomAccordion
                key={member.id}
                imageContent={getMemberImage(member)}
                expanded={expandedId === member.id}
                onChange={handleChange(member.id)}
                summaryContent={
                  <Typography sx={mobileSummaryName}>
                    {member.full_name}
                  </Typography>
                }
                details={mobileDetails(member)}
                actions={getActionButtons(member)}
              />
            ))}
          </Box>
        )
      ) : isLoading ? (
        <SkeletonTable columns={columns} rowCount={5} />
      ) : (
        <CustomTable
          columns={columns}
          data={members}
          containerRef={containerRef}
        />
      )}
    </>
  );
};

export default MyMembersCard;
